import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { deletePlan, getPlans } from '../../redux/plans/planActions'
import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import LaunchIcon from '@mui/icons-material/Launch'
import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import '../../i18n'
import DeleteButton from '../../components/common/DeleteButton'
import PaginationComp from '../../components/common/pagination'
import UseAlert from '../../components/common/alert'
import { UserRole } from '../../types/keycloak'
import { hasRole } from '../../utils/checkRole'
import { formatTitle } from '../../utils/formatTitle'
import { Plan } from '../../types/planTypes'
import {
  ListPageShell,
  listPageIconBtnStyle,
} from '../../components/common/ListPageShell'
import {
  Language,
  maturiteLabels,
  priorityLabelsTranslations,
} from '../../utils/constants'
import i18n from '../../i18n'
import '../../components/common/ListPageTable.css'
import TableEmptyState from '../../components/common/TableEmptyState'
import TableLoadingState from '../../components/common/TableLoadingState'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import { ListPageDateCell } from '../../components/common/ListPageCells'
import { onClickableTableRow } from '../../utils/tableRowClick'
import { useTableLoadingGate } from '../../hooks/useTableLoadingGate'

const PRIORITY_PILL: Record<number, { bg: string; color: string; label: string }> = {
  1: { bg: '#FAE6E3', color: '#96271B', label: 'Critical' },
  2: { bg: '#FAEFDA', color: '#84540A', label: 'High' },
  3: { bg: '#E6EFF8', color: '#22548A', label: 'Medium' },
  4: { bg: '#E1F4F0', color: '#076B5C', label: 'Low' },
}

const STATUS_PILL: Record<string, { bg: string; color: string }> = {
  'A faire': { bg: '#E1F4F0', color: '#076B5C' },
  'En cours': { bg: '#E6EFF8', color: '#22548A' },
  Terminé: { bg: '#E1F4F0', color: '#076B5C' },
  Suspendu: { bg: '#EAF0F0', color: '#5F7484' },
  'En attente': { bg: '#FAEFDA', color: '#84540A' },
}

const MATURITY_PILL: Record<string, { bg: string; color: string }> = {
  '1': { bg: '#EDE7F6', color: '#5E35B1' },
  '2': { bg: '#E6EFF8', color: '#22548A' },
  '3': { bg: '#E1F4F0', color: '#076B5C' },
  '4': { bg: '#FAEFDA', color: '#84540A' },
  '5': { bg: '#FAE6E3', color: '#96271B' },
  Initiation: { bg: '#EDE7F6', color: '#5E35B1' },
  Definition: { bg: '#E6EFF8', color: '#22548A' },
  Définition: { bg: '#E6EFF8', color: '#22548A' },
  Integration: { bg: '#E1F4F0', color: '#076B5C' },
  Intégration: { bg: '#E1F4F0', color: '#076B5C' },
  Management: { bg: '#FAEFDA', color: '#84540A' },
  Optimization: { bg: '#FAE6E3', color: '#96271B' },
  Optimisation: { bg: '#FAE6E3', color: '#96271B' },
}

function Pill({
  label,
  bg,
  color,
}: {
  label: string
  bg: string
  color: string
}) {
  return (
    <span className="incyia-pill" style={{ background: bg, color }}>
      <span className="incyia-pill-dot" />
      {label}
    </span>
  )
}

function maturityLabel(value: string | undefined, lang: string): string {
  if (!value) return '—'
  const fromMap = maturiteLabels[value]
  if (fromMap) {
    return fromMap[lang as Language] || fromMap.en
  }
  return value
}

export default function PlanListe() {
  const { t } = useTranslation()
  const locale = i18n.language
  const dispatch: AppDispatch = useAppDispatch()
  const navigate = useNavigate()
  const roles = useSelector((state: RootState) => state.auth.roles)

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [search, setSearch] = useState('')
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  const plansList = useSelector((state: RootState) => state.plan.PlanResponse)
  const loading = useSelector((state: RootState) => state.plan.loading)
  const rows = plansList?.plans ?? []
  const showLoader = useTableLoadingGate(loading, [page, pageSize, search])

  useEffect(() => {
    dispatch(getPlans({ search, page, pageSize }))
  }, [dispatch, page, pageSize, search])

  const canManage =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.SSI_TEAM) ||
    hasRole(roles, UserRole.ADMIN)

  const handleDeleteClick = async (planId: string) => {
    try {
      dispatch(deletePlan(planId))
      setAlert({
        open: true,
        message: t('alerts.planDelete.success'),
        severity: 'success',
      })
    } catch {
      setAlert({
        open: true,
        message: t('alerts.planDelete.error'),
        severity: 'error',
      })
    }
  }

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert({ ...alert, open: false })}
      />

      <ListPageShell
        eyebrow={t('entityForms.plan.createEyebrow', {
          defaultValue: 'Management · Risk treatment plans',
        })}
        title={t('listPages.plansTitle')}
        titleTourId="plans-page-header"
        subtitle={t('listPages.plansSub')}
        addLabel={canManage ? t('buttons.addPlan') : undefined}
        onAdd={canManage ? () => navigate('/addplan') : undefined}
        search={search}
        onSearchChange={(value) => {
          setSearch(value)
          setPage(1)
        }}
        searchPlaceholder={t('filterByName')}
        countLabel={t('listPages.plansCount', {
          count: plansList?.total ?? 0,
        })}
        footer={
          (plansList?.totalPages as number) > 0 ? (
            <PaginationComp
              page={page}
              pageSize={pageSize}
              onChange={(nextPage, newSize) => {
                setPage(nextPage)
                setPageSize(newSize)
              }}
              totalItems={plansList?.totalPages as number}
            />
          ) : undefined
        }
      >
        <div className="incyia-table-wrap">
          {showLoader ? (
            <TableLoadingState />
          ) : rows.length === 0 ? (
            <TableEmptyState />
          ) : (
            <table className="incyia-table">
              <thead>
                <tr>
                  <th>{t('plan_name')}</th>
                  <th>{t('Description')}</th>
                  <th>{t('Start Date')}</th>
                  <th>{t('End Date')}</th>
                  <th>{t('Priority')}</th>
                  <th>{t('maturity_objective')}</th>
                  <th>{t('Status')}</th>
                  <th>{t('Mission Name')}</th>
                  <th style={{ textAlign: 'right' }}>{t('Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((plan: Plan) => {
                  const priority = Number(plan.Priority)
                  const priorityStyle =
                    PRIORITY_PILL[priority] ?? PRIORITY_PILL[4]
                  const statusStyle = STATUS_PILL[plan.Status || ''] ?? {
                    bg: '#EAF0F0',
                    color: '#5F7484',
                  }
                  const matKey = String(plan.ObjectifMaturite || '')
                  const matStyle = MATURITY_PILL[matKey] ?? {
                    bg: '#EAF0F0',
                    color: '#5F7484',
                  }
                  const matLabel = maturityLabel(matKey, locale)

                  return (
                    <tr key={plan._id} onClick={onClickableTableRow}>
                      <td className="incyia-col-pinned">
                        <NavLink
                          to={`/plan-detail/${plan._id}`}
                          className="incyia-name-link"
                        >
                          <ListPageDescription
                            value={
                              plan.PlanName
                                ? formatTitle(plan.PlanName)
                                : undefined
                            }
                            tone="primary"
                          />
                        </NavLink>
                      </td>
                      <td className="incyia-wrap">
                        <ListPageDescription value={plan.Description} />
                      </td>
                      <td>
                        <ListPageDateCell value={plan.DateDebut} locale={locale} />
                      </td>
                      <td>
                        <ListPageDateCell value={plan.DateFin} locale={locale} />
                      </td>
                      <td>
                        <Pill
                          label={
                            t(`priority.${priority}`, {
                              defaultValue:
                                priorityLabelsTranslations[priority] ||
                                priorityStyle.label,
                            }) || priorityStyle.label
                          }
                          bg={priorityStyle.bg}
                          color={priorityStyle.color}
                        />
                      </td>
                      <td>
                        <Pill
                          label={matLabel}
                          bg={matStyle.bg}
                          color={matStyle.color}
                        />
                      </td>
                      <td>
                        <Pill
                          label={t(plan.Status || '', {
                            defaultValue: plan.Status || '—',
                          })}
                          bg={statusStyle.bg}
                          color={statusStyle.color}
                        />
                      </td>
                      <td className="incyia-muted">
                        <ListPageDescription value={plan.MissionName} />
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="incyia-row-actions">
                          <button
                            type="button"
                            title={t('listPages.openAction')}
                            onClick={() =>
                              navigate(`/plan-detail/${plan._id}`)
                            }
                            style={listPageIconBtnStyle}
                          >
                            <LaunchIcon sx={{ fontSize: 15 }} />
                          </button>
                          {canManage && (
                            <button
                              type="button"
                              title={t('listPages.edit')}
                              onClick={() => navigate(`/plan/${plan._id}`)}
                              style={listPageIconBtnStyle}
                            >
                              <DriveFileRenameOutlineOutlinedIcon
                                sx={{ fontSize: 15 }}
                              />
                            </button>
                          )}
                          {canManage && (
                            <DeleteButton
                              onDelete={() =>
                                handleDeleteClick(plan._id as string)
                              }
                              item={{
                                entityType: t('plans', {
                                  defaultValue: 'Plan',
                                }),
                                name: plan.PlanName
                                  ? formatTitle(plan.PlanName)
                                  : undefined,
                                details: [
                                  {
                                    label: t('deleteConfirm.fields.priority'),
                                    value: String(plan.Priority ?? '—'),
                                  },
                                  {
                                    label: t('deleteConfirm.fields.status'),
                                    value: plan.Status || '—',
                                  },
                                ],
                              }}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </ListPageShell>
    </>
  )
}
